import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHead } from "@/pages/AppLayout";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Check, Zap, Ticket, Gift, Crown } from "lucide-react";

export default function Billing() {
  const { fleet, loadFleet } = useAuth();
  const [plans, setPlans] = useState([]);
  const [testMode, setTestMode] = useState(false);
  const [period, setPeriod] = useState("monthly");
  const [loading, setLoading] = useState("");
  const [portalLoading, setPortalLoading] = useState(false);
  const [code, setCode] = useState("");
  const [discount, setDiscount] = useState(null);

  useEffect(() => { api.get("/plans").then(({ data }) => { setPlans(data.plans || []); setTestMode(!!data.test_mode); }); }, []);

  const applyCode = async () => {
    if (!code.trim()) return;
    try {
      const { data } = await api.get(`/discounts/validate/${code.trim()}`);
      if (data.valid) { setDiscount(data); toast.success(`${data.percent_off}% off applied — shown at checkout`); }
      else { setDiscount(null); toast.error("Invalid or expired code"); }
    } catch { toast.error("Could not validate code"); }
  };

  const upgrade = async (planKey) => {
    setLoading(planKey);
    try {
      const { data } = await api.post("/payments/checkout", { plan: planKey, period, origin_url: window.location.origin, discount_code: discount?.code || null });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); setLoading(""); }
  };

  const manageSubscription = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); setPortalLoading(false); }
  };

  const cancel = async () => {
    try { await api.post("/billing/cancel"); await loadFleet(); toast.success("Reverted to Free Demo plan"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };

  const grouped = { Free: [], Individual: [], Business: [] };
  plans.forEach((p) => grouped[p.tier]?.push(p));

  return (
    <div className="p-5 md:p-10 pb-24" data-testid="billing-page">
      <PageHead title="Billing" sub="One plan covers your whole fleet. Priced in AUD." />

      <div className="card p-6 mb-8 border-t-4 border-[#FFB800] flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-widest text-[#94A3B8]">Current Plan</p>
          <div className="flex items-center gap-3 flex-wrap">
            <p className="font-head font-900 text-4xl uppercase tracking-tight">{fleet?.plan_name}</p>
            {fleet?.plan === "comp" && (
              fleet?.plan_tier === "Owner"
                ? <span className="badge flex items-center gap-1 bg-[#FFB800]/15 text-[#FFB800] border-[#FFB800]/40" data-testid="billing-plan-badge"><Crown className="w-3 h-3" /> Owner Access</span>
                : <span className="badge flex items-center gap-1 bg-[#00E676]/15 text-[#00E676] border-[#00E676]/40" data-testid="billing-plan-badge"><Gift className="w-3 h-3" /> Complimentary</span>
            )}
          </div>
          <p className="font-mono text-sm text-[#FFB800] mt-1">{fleet?.machine_count} / {fleet?.machine_limit >= 1000000 ? "∞" : fleet?.machine_limit} machines used</p>
          {fleet?.plan === "comp" && <p className="font-body text-sm text-[#00E676] mt-2">{fleet?.plan_tier === "Owner" ? "Owner access" : "Complimentary access"} — unlimited machines &amp; staff, no charge.</p>}
          {fleet?.plan !== "free" && fleet?.plan !== "comp" && fleet?.billing_period && (
            <p className="font-mono text-xs text-[#94A3B8] mt-2">Billed {fleet.billing_period === "year" ? "yearly" : "monthly"}</p>
          )}
        </div>
        {fleet?.plan !== "free" && fleet?.plan !== "comp" && (
          <div className="flex items-center gap-2 flex-wrap">
            {fleet?.has_subscription && (
              <button onClick={manageSubscription} disabled={portalLoading} className="btn-ghost h-12 px-5 font-head border border-white/15" data-testid="manage-subscription-btn">{portalLoading ? "Opening…" : "Manage Subscription"}</button>
            )}
            <AlertDialog>
              <AlertDialogTrigger asChild><button className="btn-ghost h-12 px-5 font-head" data-testid="cancel-plan-btn">Cancel Plan</button></AlertDialogTrigger>
              <AlertDialogContent className="bg-[#1A1C23] border border-white/10 text-white">
                <AlertDialogHeader><AlertDialogTitle className="font-head uppercase">Cancel subscription?</AlertDialogTitle>
                  <AlertDialogDescription className="text-[#94A3B8]">You&apos;ll revert to the Free Demo (2 machines). Machines over the limit stay but you can&apos;t add more.</AlertDialogDescription></AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="btn-ghost border-white/20">Keep Plan</AlertDialogCancel>
                  <AlertDialogAction onClick={cancel} className="bg-[#FF3B30] text-white hover:bg-[#FF3B30]/80" data-testid="confirm-cancel-plan">Cancel Plan</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}
      </div>

      {fleet?.plan !== "comp" && (
        <div className="flex items-center justify-center mb-6" data-testid="billing-period-toggle">
          <div className="inline-flex items-center border border-white/15 rounded-full p-1 bg-[#1A1C23]">
            <button onClick={() => setPeriod("monthly")} className={`px-5 h-10 rounded-full font-head text-sm uppercase tracking-wide transition-colors ${period === "monthly" ? "bg-[#FFB800] text-black" : "text-[#94A3B8]"}`} data-testid="period-monthly">Monthly</button>
            <button onClick={() => setPeriod("yearly")} className={`px-5 h-10 rounded-full font-head text-sm uppercase tracking-wide transition-colors flex items-center gap-2 ${period === "yearly" ? "bg-[#FFB800] text-black" : "text-[#94A3B8]"}`} data-testid="period-yearly">Yearly <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${period === "yearly" ? "bg-black/20 text-black" : "bg-[#00E676]/15 text-[#00E676]"}`}>2 MONTHS FREE</span></button>
          </div>
        </div>
      )}

      {fleet?.plan !== "comp" && (
        <div className="card p-5 mb-8 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="discount-entry">
          <div className="flex items-center gap-2 flex-1">
            <Ticket className="w-5 h-5 text-[#FFB800]" />
            <input className="il-input font-mono uppercase" placeholder="Have a discount code?" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} data-testid="billing-discount-input" />
          </div>
          <button onClick={applyCode} className="btn-ghost h-13 px-5 font-head" style={{ height: "3.25rem" }} data-testid="billing-discount-apply">Apply</button>
          {discount && <span className="badge badge-healthy" data-testid="billing-discount-applied">{discount.code} · {discount.percent_off}% OFF</span>}
        </div>
      )}

      {Object.entries(grouped).map(([tier, list]) => list.length > 0 && (
        <div key={tier} className="mb-8">
          <h2 className="font-head font-800 text-2xl uppercase tracking-wide mb-4 flex items-center gap-2"><Zap className="w-5 h-5 text-[#FFB800]" /> {tier}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {list.map((p) => {
              const current = fleet?.plan === p.key;
              return (
                <div key={p.key} className={`p-6 border ${current ? "bg-[#1A1C23] border-[#FFB800]" : "card"}`} data-testid={`plan-${p.key}`}>
                  <div className="flex items-center justify-between">
                    <h3 className="font-head font-700 text-xl uppercase">{p.name}</h3>
                    {current && <span className="badge badge-upcoming">Current</span>}
                  </div>
                  <div className="mt-3 flex items-baseline gap-1">
                    {p.price === 0 ? (
                      <>
                        <span className="font-mono text-4xl">A$0</span>
                        <span className="font-mono text-[#94A3B8] text-sm">AUD</span>
                      </>
                    ) : period === "yearly" ? (
                      <>
                        <span className="font-mono text-4xl">A${p.annual}</span>
                        <span className="font-mono text-[#94A3B8] text-sm">/yr AUD</span>
                      </>
                    ) : (
                      <>
                        <span className="font-mono text-4xl">A${p.price}</span>
                        <span className="font-mono text-[#94A3B8] text-sm">/mo AUD</span>
                      </>
                    )}
                  </div>
                  {p.price > 0 && period === "yearly" && (
                    <p className="font-mono text-xs text-[#00E676] mt-1">Save A${p.annual_full - p.annual}/yr vs monthly</p>
                  )}
                  <p className="font-mono text-sm text-[#FFB800] mt-2 flex items-center gap-1"><Check className="w-4 h-4" /> {p.limit >= 1000000 ? "Unlimited" : p.limit} machines</p>
                  {p.price === 0 ? (
                    <button disabled className="btn-ghost w-full h-12 font-head mt-5 opacity-50">Free Tier</button>
                  ) : current ? (
                    <button disabled className="w-full h-12 font-head mt-5 bg-[#00E676]/20 text-[#00E676] border border-[#00E676]/40 uppercase">Active</button>
                  ) : (
                    <button onClick={() => upgrade(p.key)} disabled={loading === p.key} className="btn-signal w-full h-12 font-head mt-5" data-testid={`upgrade-${p.key}`}>{loading === p.key ? "Redirecting…" : `Subscribe ${period === "yearly" ? "Yearly" : "Monthly"}`}</button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
      {testMode
        ? <p className="font-body text-xs text-[#94A3B8] mt-4" data-testid="billing-test-hint">Secure checkout via Stripe (TEST MODE). Test card: 4242 4242 4242 4242, any future expiry & CVC.</p>
        : <p className="font-body text-xs text-[#94A3B8] mt-4" data-testid="billing-secure-note">Secure checkout via Stripe.</p>}
    </div>
  );
}
